<cs-header>
  <cs-header-title>{{ isIngredientMacro(ingredient) ? LL.editIngredient.logMacrosTitle() : (ingredient?.name
  || LL.editIngredient.title()) }}</cs-header-title>
  @if (isIngredientReal(ingredient)) {
    <cs-header-content>
      <div class="subtitle-row">
        <div class="amount-info">
          @if (customIngredientUnits.length > 0 && custom_unit) {
            <cs-input-measure class="header-measure" [readonly]="true"
              [useCustomReadonlyLayout]="true" [makeBallpark]="true" type="foodweight_g"
              [customUnitConfigs]="customIngredientUnits" [(canonValue)]="ingredient.weight_g" [(userUnit)]="custom_unit">
            </cs-input-measure>
          }
          @if (customIngredientUnits.length > 0 && custom_unit) {
            <span class="separator">|</span>
          }
          <cs-input-measure class="header-measure" [readonly]="true" [useCustomReadonlyLayout]="true" type="foodweight_g"
            [lockUnit]="true" [(userUnit)]="weight_unit" [(canonValue)]="ingredient.weight_g">
          </cs-input-measure>
        </div>
        <cs-button tertiary [label]="LL.editIngredient.viewDetails()" (click)="handleViewDetails()"></cs-button>
        @if (!sourceLogMeal) {
          <cs-icon-button class="more-options" icon="ellipsis-vertical"
          (click)="handleMoreOptions($event)"></cs-icon-button>
        }
      </div>
    </cs-header-content>
  }
</cs-header>

<cs-content [loading]="loadingMeals">
  <cs-item-list>
    <!-- Amount Section. First on the page, above the macros and above everything else
         (#4967): it used to sit at the foot of the page below both macro cards, so the one
         field the user came here to change had to be scrolled to.
         Hidden for log meal previews — those ingredients are added as-is.

         Both fields declare the same `input-sequence` GROUP. They show ONE value in two
         units, so the app's on-screen keypad must not offer a hop from one to the other as
         if that were a step forward; instead, finishing in either goes straight to the Log
         button, which carries a bare `input-sequence` at the foot of this file. The rule is
         in `util/input-sequence.ts`. -->
    @if (isIngredientReal(ingredient) && !sourceLogMeal) {
      <cs-item-group [title]="LL.editIngredient.amount()">
        <div class="measure-inputs">
          <!-- #5544: while "Weighed with peel" is ticked this field holds the weight as TYPED, with
               the peel; the eaten weight it converts to is ingredient.weight_g, which the measure
               field below and the header keep showing. -->
          @if (isWeighedWithRefuseTicked) {
            <cs-input-measure #amountMeasure input-sequence="amount" type="foodweight_g"
              [(canonValue)]="typedWeight_g" [(userUnit)]="weight_unit"
              (editDone)="onTypedWeightChange()" (userUnitChange)="onTypedWeightChange()" [emitDoneEventOnEveryKeypress]="true"
              [unitChangeConvertsValue]="ingredient.locked">
            </cs-input-measure>
          } @else {
            <cs-input-measure #amountMeasure input-sequence="amount" type="foodweight_g"
              [(canonValue)]="ingredient.weight_g" [(userUnit)]="weight_unit"
              (editDone)="onWeightChange()" (userUnitChange)="onWeightChange()" [emitDoneEventOnEveryKeypress]="true"
              [unitChangeConvertsValue]="ingredient.locked">
            </cs-input-measure>
          }
          @if (customIngredientUnits.length > 0) {
            <cs-input-measure input-sequence="amount" type="foodweight_g"
              [customUnitConfigs]="customIngredientUnits" [(canonValue)]="ingredient.weight_g" [(userUnit)]="custom_unit"
              (editDone)="onWeightChange()" (userUnitChange)="onWeightChange()" [emitDoneEventOnEveryKeypress]="true"
              [unitChangeConvertsValue]="ingredient.locked">
            </cs-input-measure>
          }
        </div>
        <!-- #5544: the food says what share is not eaten, so offer to take it off a weight taken with it -->
        @if (weighedWithLabel) {
          <!-- the label reads like the "Amount" heading above it, the box sits at the right, and the
               whole row toggles it: the checkbox's own tap cover stretches over the row (see the scss) -->
          <div class="weighed-with-row">
            <cs-label class="weighed-with-label">{{ weighedWithLabel }}</cs-label>
            <cs-checkbox class="weighed-with-checkbox" [checked]="isWeighedWithRefuseTicked"
              (checkedChange)="onWeighedWithRefuseChange($event)"></cs-checkbox>
          </div>
          <!-- the same info card as the hint below, so ticking the box swaps the card's text rather
               than turning the card into plain text -->
          @if (weighedWithNote) {
            <cs-info-card class="weighed-with-note" type="info" [message]="weighedWithNote" [dismissible]="false">
            </cs-info-card>
          }
        }
        <!-- food staples plan 4a: what to take off before weighing, and how much of the weight that is.
             Hidden while the box above is ticked: "weigh it without" would contradict it. -->
        @if (refuseHint && !isWeighedWithRefuseTicked) {
          <cs-info-card class="refuse-hint" type="info" [message]="refuseHint" [dismissible]="false">
          </cs-info-card>
        }
      </cs-item-group>
    }

    <!-- Log Mode: Details Section -->
    @if (mode === EditIngredientMode.Log) {
      <cs-item-group [title]="LL.editIngredient.details()">
        <div class="details-fields">
          <cs-form-field [label]="LL.editIngredient.date()">
            <input-calendar [(date)]="dayDate" (dateChange)="handleDayDateChange($event)">
          </input-calendar>
        </cs-form-field>
        <cs-form-field [label]="LL.editIngredient.meal()">
          <cs-select [(value)]="selectedLogMeal" (valueChange)="handleMealChange()">
            @for (logMeal of selectableLogMeals; track logMeal) {
              <cs-select-option [value]="logMeal"
              [label]="mealDisplayName(logMeal)"></cs-select-option>
            }
          </cs-select>
        </cs-form-field>
      </div>
    </cs-item-group>
  }

  <!-- Macro Mode Content -->
  @if (isIngredientMacro(ingredient)) {
    <!-- Food Name Input -->
    <cs-form-field [label]="LL.editIngredient.foodName()" class="food-name-field">
      <cs-input [placeholder]="LL.common.placeholders.enterFoodName()" [(value)]="ingredient.name"
        (valueChange)="handleCustomFoodNameChange()" />
      </cs-form-field>
      <!-- Nutrient Inputs -->
      <div class="macro-nutrient-inputs">
        @for (displayNutrient of displayNutrientsCustom; track trackByNutrientTag($index, displayNutrient)) {
          <div class="nutrient-input">
            <cs-form-field [label]="displayNutrient.name">
              <cs-input-measure [type]="mapNutrientUnitIdToMeasurementType(displayNutrient.unitId)" [lockUnit]="true"
                [(canonValue)]="ingredient.nutrients[displayNutrient.tag]" (canonValueChange)="onCustomNutrientChange()">
              </cs-input-measure>
            </cs-form-field>
          </div>
        }
      </div>
      <!-- Validation Warning -->
      @if (errorMessage) {
        <cs-info-card type="warning" [message]="errorMessage" [dismissible]="false">
        </cs-info-card>
      }
    }

    <!-- Real Ingredient Mode Content -->
    @if (isIngredientReal(ingredient)) {
      <!-- df-6243048bcd73: the food could not be read, or no longer exists. Drawn here, in the
           real-ingredient section, because that is the only kind of ingredient it is set for. -->
      @if (foodLoadProblem) {
        <cs-info-card type="warning" [message]="foodLoadProblem" [dismissible]="false">
        </cs-info-card>
      }
      <!-- #4541: this food states calories its own macros contradict. Shown before the
           macro card so it is read before the numbers it is warning about. -->
      @if (inconsistentNutrientsWarning) {
        <cs-info-card type="warning" [message]="inconsistentNutrientsWarning" [dismissible]="false">
        </cs-info-card>
      }
      <!--
        Macros. One card in the gap above the keypad, with two dials picking
        what it describes. It replaced two stacked cards, of which the keypad
        covered the second one outright. Design: design-docs/nutrient-bars.md.
      -->
      <cs-item-group>
        @if (canShowScopeDials) {
          <!--
            Both dials are hidden together when there is nothing wider to
            compare against -- no meal targets AND no day targets -- rather than
            shown doing nothing. Either one on its own is enough to draw them, so
            a day with targets above a meal without them shows Food and Day.

            On the food scope the mode dial is switched off, because a single
            food has no target for "left" to be measured against. Switched off
            and not removed: removing it let the scope dial expand into the
            freed space and the whole row jumped every time the scope changed.
            `[disabled]` fades the control and makes it unreachable while it
            keeps its space, so nothing moves at all. It used to be made
            invisible instead; the mockup shows it faded, and a control the
            reader can see but not use says more than an empty gap (owner
            instruction, 2026-09-09).

            The scope dial is bound to the EFFECTIVE scope, not the remembered
            one. A remembered scope whose position is not on offer here would
            otherwise leave the dial with no segment highlighted at all, while
            the card below showed Food.
          -->
          <div class="macro-card-dials">
            <cs-segment [selection]="effectiveMacroScope" (selectionChange)="onMacroScopeChange($event)">
              @for (segment of macroScopeSegments; track segment.value) {
                <cs-segment-button [value]="segment.value">{{ segment.label }}</cs-segment-button>
              }
            </cs-segment>
            <cs-segment [(selection)]="macroMode" (selectionChange)="onMacroModeChange()"
              [disabled]="effectiveMacroScope === MacroCardScope.Food">
              <cs-segment-button [value]="NutrientDisplayMode.Logged">{{ LL.nutrientList.total() }}</cs-segment-button>
              <cs-segment-button [value]="NutrientDisplayMode.Remaining">{{ LL.nutrientList.left() }}</cs-segment-button>
            </cs-segment>
          </div>
        }
        <div class="bordered-box">
          <nutrient-list class="nutrient-graph" [nutrients]="macroCardNutrients" state="compact-macros"
            [barStyle]="macroCardBarStyle" [displayMode]="macroCardDisplayMode" [expandable]="true">
          </nutrient-list>
        </div>
      </cs-item-group>
      <!-- Foods Section (for recipes that can be split — for log meals, only show if meal has no other ingredients) -->
      @if (canSplitFood && (!sourceLogMeal || ingredients.length === 0)) {
        <cs-item-group [title]="LL.editIngredient.foods()">
          <div class="split-empty-state">
            <cs-icon name="split"></cs-icon>
            <!-- With no plan targets there is nothing for the tuner to aim at, so only the plain
                 split is offered and the wording stops promising tuned amounts. Offering
                 "Split & Auto-Tune" there split the foods, left every amount exactly as it was,
                 and said nothing (df-fc1cd793ae3b). -->
            @if (sourceLogMeal) {
              <div class="split-text">
                <span class="split-description">{{ canAutoTuneSplit ? LL.editIngredient.splitMealDescription() : LL.editIngredient.splitMealDescriptionNoTargets() }}</span>
              </div>
              <div class="split-buttons">
                @if (canAutoTuneSplit) {
                  <cs-button small secondary [label]="LL.editIngredient.splitAndAutoTune()"
                  (click)="handleSplitAndAutoTune()"></cs-button>
                } @else {
                  <cs-button small secondary [label]="LL.editIngredient.splitIntoFoods()"
                  (click)="handleSplitIntoFoods()"></cs-button>
                }
              </div>
            } @else {
              <div class="split-text">
                <span class="split-title">{{ LL.editIngredient.splitTitle() }}</span>
                <span class="split-description">{{ LL.editIngredient.splitDescription() }}</span>
              </div>
              <div class="split-buttons">
                <cs-button small secondary [label]="LL.editIngredient.splitIntoFoods()"
                (click)="handleSplitIntoFoods()"></cs-button>
                @if (canAutoTuneSplit) {
                  <cs-button small secondary [label]="LL.editIngredient.splitAndAutoTune()"
                  (click)="handleSplitAndAutoTune()"></cs-button>
                }
              </div>
            }
          </div>
        </cs-item-group>
      }
    }
  </cs-item-list>
</cs-content>

<!-- One button, the primary Log (#4967). The Cancel that used to sit beside it on a wide
     screen is gone: the header's top-left back arrow is now the one way out, the same on
     every screen size. `input-sequence` with no group name lets the on-screen keypad borrow
     this button, so finishing the amount on the keypad logs the food in that same press
     instead of needing an Accept first. -->
<cs-page-action-buttons>
  <cs-button medium primary input-sequence [disabled]="hasUnusableAmount"
    [label]="mode === EditIngredientMode.Edit ? LL.common.buttons.save() : LL.common.buttons.log()"
  (click)="handleSave()"></cs-button>
</cs-page-action-buttons>
